import { DateTime } from 'luxon'
import { useSelector } from '../../../store/hooks'
import { Contract, Contracts } from '../../../modules/buyerContracts/types'
import { formatDateString, mapContractStatus } from './BuyerDetailTabs'
import ContractCard from './Contracts'
import { ExternalLink } from '@labrav/react-components'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CircularProgress } from '@mui/material'
import { debounce } from 'lodash'
import { useStyles } from './Contracts.styles'

const ITEMS_PER_PAGE = 15

export const ContractsList = ({
  visibleContractData,
  setVisibleContractData,
  contractPage,
  setContractPage,
}: {
  visibleContractData: Contract[]
  setVisibleContractData: Dispatch<SetStateAction<Contract[]>>
  contractPage: number
  setContractPage: Dispatch<SetStateAction<number>>
}) => {
  const { classes } = useStyles()
  const timeZone = useSelector<string>(
    state => state.userProfile.userProfile?.timeZone || 'UTC'
  )
  const { contracts } = useSelector<Contracts>(state => state.BuyerContracts)

  const loaderRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const isFetchingRef = useRef(false)

  const hasMore = visibleContractData.length < contracts.length

  useEffect(() => {
    const initialData = contracts.slice(0, contractPage * ITEMS_PER_PAGE)
    setVisibleContractData(initialData)
  }, [contracts, contractPage, setVisibleContractData])

  const fetchMoreData = useCallback(() => {
    if (!hasMore || isFetchingRef.current) return

    isFetchingRef.current = true
    setIsLoading(true)

    const nextPage = contractPage + 1
    const nextData = contracts.slice(0, nextPage * ITEMS_PER_PAGE)

    setTimeout(() => {
      setVisibleContractData(nextData)
      setContractPage(nextPage)
      setIsLoading(false)
      isFetchingRef.current = false
    }, 300)
  }, [
    contractPage,
    contracts,
    hasMore,
    setContractPage,
    setVisibleContractData,
  ])

  const handleScroll = useCallback(
    debounce(() => {
      const container = loaderRef.current
      if (!container || isFetchingRef.current) return

      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore) {
        fetchMoreData()
      }
    }, 100),
    [fetchMoreData, hasMore]
  )

  useEffect(() => {
    const container = loaderRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
    }
    return () => {
      container?.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const isExpired = (expirationDate: string): boolean => {
    const expirationDateTime = DateTime.fromISO(expirationDate, {
      zone: timeZone,
    })
    const now = DateTime.now().setZone(timeZone)
    return expirationDateTime < now
  }

  return (
    <>
      <div ref={loaderRef} className={classes.contractListWrapper}>
        <div
          className={classes.contractList}
          style={{
            paddingBottom: isLoading ? '20px' : '0px',
          }}
        >
          {visibleContractData.map(contract => (
            <ContractCard
              key={contract.contractId}
              name={contract.contractName}
              Id={contract.contractId}
              contractIdentifier={contract.contractIdentifier}
              productId={contract.productId}
              status={mapContractStatus(contract.contractStatus)}
              stages={[
                {
                  status: contract?.offerSentAt ? 'sent' : 'started',
                  toolTipLabel: `Offer sent on ${formatDateString(
                    contract?.offerSentAt,
                    timeZone
                  )}`,
                },
                {
                  status: contract?.offerViewedAt ? 'sent' : 'started',
                  toolTipLabel: `Offer viewed on ${formatDateString(
                    contract?.offerViewedAt,
                    timeZone
                  )}`,
                },
                {
                  status: contract?.offerAcceptedAt ? 'sent' : 'started',
                  toolTipLabel: `Offer accepted on ${formatDateString(
                    contract?.offerAcceptedAt,
                    timeZone
                  )}`,
                },
                {
                  status: contract?.contractStartDate ? 'sent' : 'started',
                  toolTipLabel: `Contract started on ${formatDateString(
                    contract?.contractStartDate,
                    timeZone
                  )}`,
                },
                {
                  status: contract?.contractExpirationDate
                    ? isExpired(contract?.contractExpirationDate)
                      ? 'failed'
                      : 'started'
                    : 'started',
                  toolTipLabel: contract?.contractExpirationDate
                    ? isExpired(contract?.contractExpirationDate)
                      ? `Contract expired on ${formatDateString(
                          contract?.contractExpirationDate,
                          timeZone
                        )}`
                      : `Contract expiring on ${formatDateString(
                          contract?.contractExpirationDate,
                          timeZone
                        )}`
                    : 'Contract expiring on -',
                },
              ]}
              tcv={contract.tcv}
              offerType={contract.offerType}
              expirationDate={contract?.expiringOn || ''}
              productName={
                <ExternalLink
                  href={`/flyout/products-listing/${contract.productId}/details`}
                >
                  {contract.productName}
                </ExternalLink>
              }
              AccountOwner={contract?.creatorName || '-'}
              increasing={false}
              autoRenew={contract.autoRenew || false}
            />
          ))}
        </div>

        {isLoading && (
          <div className={classes.loaderWrapper}>
            <CircularProgress size={32} className={classes.loadingSpinner} />
          </div>
        )}
      </div>
    </>
  )
}
